import React from "react";
import MarketingHeader from "@/components/MarketingHeader";

const TIERS = [
  {
    name: "Free",
    tagline: "Try it on your next solicitation.",
    price: "$0",
    priceUnit: "/month",
    priceNote: "No card required",
    cta: "Start free",
    ctaHref: "/register?plan=free",
    users: "1 — solo use only",
    proposals: 1,
    proposalsCap: 20,
    features: "Administrator role only. Basic DOCX export. No Compliance Matrix export.",
  },
  {
    name: "Team",
    tagline: "For small proposal teams juggling a few live pursuits.",
    price: "$299",
    priceUnit: "/month",
    priceNote: "Billed monthly or annually",
    cta: "Start Team",
    ctaHref: "/register?plan=team",
    users: "Up to 5",
    proposals: 10,
    proposalsCap: 20,
    sync: "Manual sync, standard daily quota",
    features: "Proposal Manager, Contributor, Reviewer, and KB Manager roles. Compliance Matrix. Multi-volume DOCX / PDF / ZIP export.",
  },
  {
    name: "Business",
    tagline: "For growing pipelines that need the full compliance toolkit.",
    price: "$599",
    priceUnit: "/month",
    priceNote: "Billed monthly or annually",
    cta: "Start Business",
    ctaHref: "/register?plan=business",
    featured: true,
    users: "Up to 10",
    proposals: 20,
    proposalsCap: 20,
    sync: "Full Sync",
    features: "All 7 roles plus Admin Console. Version history, color-team snapshots, and audit log. Everything in Team.",
  },
  {
    name: "Enterprise",
    tagline: "For scaled GovCon operations across multiple orgs.",
    price: "Custom",
    priceNote: "Annual contract",
    cta: "Talk to sales",
    ctaHref: "/#demo",
    users: "Unlimited",
    proposals: null,
    sync: "Dedicated API key and quota",
    features: "Multi-org / multi-tenant management with SSO. FedRAMP-moderate security posture. Dedicated success manager and SLA.",
  },
];

export default function Pricing() {
  return (
    <div className="mkt-page" data-testid="page-pricing">
      <MarketingHeader />

      <section className="mkt-hero">
        <div className="mkt-eyebrow">◢ PRICING</div>
        <h1 className="mkt-h1">Plans sized to how many proposals you actually ship</h1>
        <p className="mkt-sub">
          Every plan includes SAM.gov opportunity discovery and AI-assisted proposal shredding. What
          changes is your team size, your monthly proposal volume, and how much of the compliance and
          collaboration toolkit you unlock.
        </p>
      </section>

      <section className="pricing-grid-wrap">
        <div className="pricing-grid">
          {TIERS.map((t) => (
            <div className={`pricing-card ${t.featured ? "featured" : ""}`} key={t.name} data-testid={`pricing-card-${t.name.toLowerCase()}`}>
              {t.featured && <span className="pricing-ribbon">Most popular</span>}
              <div className="pricing-tier-name">{t.name}</div>
              <p className="pricing-tagline">{t.tagline}</p>
              <div className="pricing-price-row">
                <span className="pricing-price">{t.price}</span>
                {t.priceUnit && <span className="pricing-price-unit">{t.priceUnit}</span>}
              </div>
              <p className="pricing-price-note">{t.priceNote}</p>
              <a
                className={`btn ${t.featured ? "btn-primary" : "btn-outline"} pricing-cta`}
                href={t.ctaHref}
                data-testid={`pricing-cta-${t.name.toLowerCase()}`}
              >
                {t.cta}
              </a>
              <div className="pricing-divider" />
              <div className="pricing-facts">
                <div>
                  <div className="pricing-fact-label">Users</div>
                  <div className="pricing-fact-value">{t.users}</div>
                </div>
                <div>
                  <div className="pricing-fact-label">Proposals / contracts per month</div>
                  {t.proposals === null ? (
                    <>
                      <div className="pricing-fact-value"><strong>Unlimited</strong></div>
                      <div className="pricing-meter">
                        <div className="pricing-meter-track"><div className="pricing-meter-fill" style={{ width: "100%" }} /></div>
                        <span className="pricing-meter-cap">∞</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="pricing-fact-value"><strong>{t.proposals}</strong> included</div>
                      <div className="pricing-meter">
                        <div className="pricing-meter-track">
                          <div className="pricing-meter-fill" style={{ width: `${(t.proposals / t.proposalsCap) * 100}%` }} />
                        </div>
                        <span className="pricing-meter-cap">{t.proposals} / {t.proposalsCap}</span>
                      </div>
                    </>
                  )}
                </div>
                {t.sync && (
                  <div>
                    <div className="pricing-fact-label">SAM.gov sync</div>
                    <div className="pricing-fact-value">{t.sync}</div>
                  </div>
                )}
                <div>
                  <div className="pricing-fact-label">Features</div>
                  <div className="pricing-fact-value">{t.features}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="pricing-footnote">
          <strong>Proposals / contracts per month</strong> counts each new solicitation you upload for
          AI-assisted requirement shredding. Roles, exports, and collaboration tools are cumulative —
          every plan includes what's listed on the tiers below it. Enterprise pricing is custom based on
          users and SAM.gov call volume.
        </p>
      </section>
    </div>
  );
}
